import type {
  PublicStatusNamespaceRecord,
  PublicStatusResponse,
  PublicStatusSkuRecord,
  PublicStatusState,
} from '@nadoutong/contracts';
import { useLocale, useT } from '../i18n/index.ts';

/**
 * Public status page — namespace rollup with SKU drill-down since epic
 * #898 P6 (#918). Each namespace renders as a section (weighted
 * availability + 24h request rollup in the heading) over a card of
 * per-SKU rows. The channel-era vendor/channel records and the bucket
 * history matrix are gone — namespace/SKU records carry availability
 * and request counts only. `namespaces` is OPTIONAL on the wire, so a
 * pre-#906 payload degrades to a graceful empty state under the intact
 * summary header.
 */

interface Props {
  readonly data: PublicStatusResponse | null;
  readonly namespaces: readonly PublicStatusNamespaceRecord[];
  readonly loading: boolean;
  readonly error: boolean;
}

export function StatusPage({ data, namespaces, loading, error }: Props): JSX.Element {
  const { locale } = useLocale();
  const timeFormatter = new Intl.DateTimeFormat(locale, {
    hour: '2-digit',
    minute: '2-digit',
  });

  return (
    <div
      data-testid="status-page"
      className="mx-auto max-w-[1210px] px-4 py-7 sm:px-6 lg:py-8"
    >
      {loading ? (
        <StatusLoading />
      ) : error || data === null ? (
        <StatusError />
      ) : (
        <div className="space-y-3">
          <StatusHeader
            data={data}
            namespaces={namespaces}
            timeFormatter={timeFormatter}
            locale={locale}
          />

          {namespaces.length === 0 ? (
            <StatusEmpty />
          ) : (
            <section className="space-y-7" data-testid="status-namespaces">
              {namespaces.map((namespace) => (
                <NamespaceCard key={namespace.namespace_id} namespace={namespace} />
              ))}
            </section>
          )}
        </div>
      )}
    </div>
  );
}

function StatusHeader(params: {
  readonly data: PublicStatusResponse;
  readonly namespaces: readonly PublicStatusNamespaceRecord[];
  readonly timeFormatter: Intl.DateTimeFormat;
  readonly locale: string;
}): JSX.Element {
  const { data, locale, namespaces, timeFormatter } = params;
  const t = useT();
  const totalRequests24h = namespaces.reduce(
    (sum, namespace) => sum + namespace.request_count_24h,
    0,
  );

  return (
    <section
      data-testid="status-summary"
      className="ghost rounded-[18px] bg-white/72 px-6 py-6 shadow-warm-sm sm:px-8"
    >
      <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="font-display text-[30px] font-semibold leading-tight tracking-[-0.02em] text-ink sm:text-[36px]">
              {t('status.header.title')}
            </h1>
          </div>
          <p className="mt-4 text-[15px] leading-[1.7] text-ink-muted">
            {t('status.summary.window', { hours: String(data.window_hours) })}
          </p>
        </div>

        <div className="flex flex-col gap-5 lg:items-end">
          <div className="flex items-center gap-4">
            <p className="text-[14px] leading-none text-ink">
              {data.updated_at === null
                ? t('status.summary.updated_pending')
                : t('status.summary.updated_recent', {
                    time: timeFormatter.format(new Date(data.updated_at)),
                    age: formatAge(data.updated_at, locale),
                  })}
            </p>
            <button
              type="button"
              aria-label={t('status.action.refresh')}
              className="ghost-strong flex h-11 w-11 items-center justify-center rounded-[12px] bg-white/70 text-ink transition hover:bg-butter"
              onClick={() => window.location.reload()}
            >
              <RefreshIcon />
            </button>
          </div>

          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-[14px] text-ink">
            <span className="inline-flex items-center gap-2">
              <StateDot state={data.summary.state} />
              <span>{t('status.summary.overall')}</span>
              <strong className="font-semibold text-ink">
                {stateLabel(data.summary.state, t)}
              </strong>
            </span>
            {namespaces.length > 0 && (
              <>
                <InlineDivider />
                <span>
                  {t('status.summary.namespaces_inline', {
                    count: formatCount(namespaces.length),
                  })}
                </span>
                <InlineDivider />
                <span>
                  {t('status.requests_24h', { count: formatCount(totalRequests24h) })}
                </span>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function StatusLoading(): JSX.Element {
  return (
    <div className="space-y-3" data-testid="status-loading">
      <div className="h-36 animate-pulse rounded-[18px] bg-white/70" />
      <div className="space-y-0">
        <div className="h-28 animate-pulse rounded-[18px] bg-white/80" />
        <div className="-mt-px h-28 animate-pulse rounded-[18px] bg-white/80" />
        <div className="-mt-px h-28 animate-pulse rounded-[18px] bg-white/80" />
      </div>
    </div>
  );
}

function StatusError(): JSX.Element {
  const t = useT();
  return (
    <section
      data-testid="status-error"
      className="ghost rounded-[18px] bg-butter/50 p-7 text-ink"
    >
      <h1 className="mb-3 font-display text-[28px] font-semibold">
        {t('status.error.title')}
      </h1>
      <p className="text-[14px] leading-[1.7] text-ink-muted">
        {t('status.error.body')}
      </p>
    </section>
  );
}

/**
 * Pre-#906 payloads carry no `namespaces[]`; render an informative
 * empty card instead of an empty section (or a crash).
 */
function StatusEmpty(): JSX.Element {
  const t = useT();
  return (
    <section
      data-testid="status-empty"
      className="ghost rounded-[18px] bg-butter/50 p-7 text-ink"
    >
      <h2 className="mb-3 font-display text-[22px] font-semibold">
        {t('status.empty.title')}
      </h2>
      <p className="text-[14px] leading-[1.7] text-ink-muted">
        {t('status.empty.body')}
      </p>
    </section>
  );
}

function NamespaceCard(params: {
  readonly namespace: PublicStatusNamespaceRecord;
}): JSX.Element {
  const { namespace } = params;
  const t = useT();

  return (
    <section
      data-testid={`status-namespace-${namespace.namespace_slug}`}
      className="space-y-2.5"
    >
      <div className="flex flex-wrap items-end justify-between gap-x-4 gap-y-1 px-2 sm:px-4">
        <h2 className="font-display text-[24px] font-semibold leading-tight tracking-[-0.02em] text-ink sm:text-[26px]">
          {namespace.namespace_display_name}
        </h2>
        <div
          className="flex items-baseline gap-4"
          data-testid={`status-namespace-rollup-${namespace.namespace_slug}`}
        >
          <span className="text-[13px] text-ink-muted">
            {t('status.requests_24h', {
              count: formatCount(namespace.request_count_24h),
            })}
          </span>
          <span
            className={[
              'text-[17px] font-semibold tracking-[-0.02em]',
              availabilityTextClass(namespace.availability_pct),
            ].join(' ')}
          >
            {formatPct(namespace.availability_pct)}%
          </span>
        </div>
      </div>

      <article className="ghost rounded-[20px] bg-white/76 px-5 py-5 shadow-warm-sm sm:px-7">
        <div className="divide-y divide-ink/10">
          {namespace.skus.map((sku) => (
            <SkuRow key={sku.sku_id} sku={sku} />
          ))}
        </div>
      </article>
    </section>
  );
}

function SkuRow(params: { readonly sku: PublicStatusSkuRecord }): JSX.Element {
  const { sku } = params;
  const t = useT();

  return (
    <div
      data-testid={`status-sku-${sku.sku_id}`}
      className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2 py-4"
    >
      <h3 className="min-w-0 break-all font-mono text-[14px] font-medium leading-tight text-ink sm:text-[15px]">
        {sku.sku_name}
      </h3>

      <div className="flex shrink-0 items-baseline gap-5">
        <span className="font-mono text-[12px] text-ink-subtle">
          {t('status.requests_24h', { count: formatCount(sku.request_count_24h) })}
        </span>
        <span
          className={[
            'text-[20px] font-semibold leading-none tracking-[-0.03em]',
            availabilityTextClass(sku.availability_pct),
          ].join(' ')}
          data-testid={`status-sku-availability-${sku.sku_id}`}
        >
          {formatPct(sku.availability_pct)}%
        </span>
      </div>
    </div>
  );
}

function InlineDivider(): JSX.Element {
  return <span className="hidden h-7 w-px bg-ink/18 sm:inline-block" aria-hidden="true" />;
}

function StateDot({ state }: { readonly state: PublicStatusState }): JSX.Element {
  return <span className={`inline-block h-2.5 w-2.5 shrink-0 rounded-full ${stateDotClass(state)}`} />;
}

function RefreshIcon(): JSX.Element {
  return (
    <svg
      aria-hidden="true"
      className="h-5 w-5"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
    >
      <path d="M20 11a8 8 0 1 0-2.34 5.66" />
      <path d="M20 4v7h-7" />
    </svg>
  );
}

function stateLabel(state: PublicStatusState, t: ReturnType<typeof useT>): string {
  switch (state) {
    case 'healthy':
      return t('status.state.healthy');
    case 'busy':
      return t('status.state.busy');
    case 'unavailable':
      return t('status.state.unavailable');
  }
}

function stateDotClass(state: PublicStatusState): string {
  switch (state) {
    case 'healthy':
      return 'bg-status-healthy';
    case 'busy':
      return 'bg-status-busy';
    case 'unavailable':
      return 'bg-status-unavailable';
  }
}

function stateTextClass(state: PublicStatusState): string {
  switch (state) {
    case 'healthy':
      return 'text-status-healthy';
    case 'busy':
      return 'text-status-busy';
    case 'unavailable':
      return 'text-status-unavailable';
  }
}

/**
 * Namespace/SKU records intentionally carry no `state` field — derive a
 * display tone from `availability_pct` using the same thresholds
 * platform-api applies to channel snapshots
 * (`apps/platform-api/src/routes/status-shared.ts`: healthy > 99.00,
 * busy >= 80.00). Presentation-only; the wire stays state-free.
 */
function availabilityTextClass(availabilityPct: number): string {
  const state: PublicStatusState =
    availabilityPct > 99 ? 'healthy' : availabilityPct >= 80 ? 'busy' : 'unavailable';
  return stateTextClass(state);
}

function formatAge(isoDate: string, locale: string): string {
  const diffMs = Date.now() - new Date(isoDate).getTime();
  const minutes = Math.max(0, Math.round(diffMs / 60_000));
  if (locale === 'zh-CN') {
    return `${minutes} 分钟前`;
  }
  return minutes === 1 ? '1 minute ago' : `${minutes} minutes ago`;
}

function formatPct(value: number): string {
  return value.toFixed(1);
}

function formatCount(value: number): string {
  return value.toLocaleString('en-US');
}
